import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const WIDTH = 700;
const HEIGHT = 400;
const MARGIN = { top: 20, right: 30, bottom: 50, left: 60 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function kernelDensityEstimator(kernel, X) {
  return function (V) {
    return X.map((x) => [x, d3.mean(V, (v) => kernel(x - v))]);
  };
}

function kernelEpanechnikov(k) {
  return function (v) {
    return Math.abs((v /= k)) <= 1 ? (0.75 * (1 - v * v)) / k : 0;
  };
}

export default function ViolinPlot({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const sorted = useMemo(() => {
    if (!data || (Array.isArray(data) && data.length === 0)) {
  const emptyStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '320px',
    background: '#f9fafb',
    border: '1px dashed #e5e7eb',
    borderRadius: '8px',
    color: '#6b7280',
    fontSize: '14px',
    fontStyle: 'italic'
  };

  return <div style={emptyStyle}>No analytical data available for this selection.</div>;
}
    return [...data].sort((a, b) => (b.median || 0) - (a.median || 0));
  }, [data]);

  useEffect(() => {
    if (!sorted || sorted.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const innerW = WIDTH - MARGIN.left - MARGIN.right;
    const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('viewBox', `0 0 ${WIDTH} ${HEIGHT}`)
      .attr('width', WIDTH)
      .attr('height', HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Collect all values for Y domain
    let allValues = [];
    sorted.forEach((d) => {
      if (d.values && d.values.length > 0) {
        allValues = allValues.concat(d.values);
      }
    });

    if (allValues.length === 0) return;

    const yExtent = d3.extent(allValues);
    const yPadding = (yExtent[1] - yExtent[0]) * 0.1 || 5;

    // Scales
    const x = d3.scaleBand()
      .domain(sorted.map((d) => d.state))
      .range([0, innerW])
      .padding(0.15);

    const y = d3.scaleLinear()
      .domain([yExtent[0] - yPadding, yExtent[1] + yPadding])
      .nice()
      .range([innerH, 0]);

    // Color scale: blue to purple
    const colorScale = d3.scaleLinear()
      .domain([0, sorted.length - 1])
      .range(['#3b82f6', '#7c3aed']);

    // KDE setup
    const ticks = y.ticks(80);
    const bandwidth = (yExtent[1] - yExtent[0]) / 8 || 2;
    const kde = kernelDensityEstimator(kernelEpanechnikov(bandwidth), ticks);

    // Compute densities and find global max for scaling
    const densities = [];
    let maxDensity = 0;
    sorted.forEach((d) => {
      if (d.values && d.values.length > 0) {
        const density = kde(d.values);
        densities.push(density);
        const localMax = d3.max(density, (p) => p[1]);
        if (localMax > maxDensity) maxDensity = localMax;
      } else {
        densities.push(null);
      }
    });

    // Violin width scale: density maps to half-width within the band
    const halfBandWidth = x.bandwidth() / 2;
    const violinWidth = d3.scaleLinear()
      .domain([0, maxDensity])
      .range([0, halfBandWidth]);

    // Horizontal grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-h')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Draw violins
    sorted.forEach((d, i) => {
      const density = densities[i];
      if (!density) return;

      const cx = x(d.state) + x.bandwidth() / 2;
      const color = colorScale(i);

      // Build violin path (symmetric around cx)
      const violinArea = d3.area()
        .curve(d3.curveBasis)
        .x0((p) => cx - violinWidth(p[1]))
        .x1((p) => cx + violinWidth(p[1]))
        .y((p) => y(p[0]));

      g.append('path')
        .datum(density)
        .attr('d', violinArea)
        .attr('fill', color)
        .attr('fill-opacity', 0.35)
        .attr('stroke', color)
        .attr('stroke-width', 1.5)
        .attr('stroke-opacity', 0.7);

      // Inner box plot: Q1-Q3 box
      const q1 = d.q1 != null ? d.q1 : d3.quantile(d.values.sort(d3.ascending), 0.25);
      const q3 = d.q3 != null ? d.q3 : d3.quantile(d.values.sort(d3.ascending), 0.75);
      const median = d.median != null ? d.median : d3.median(d.values);
      const mean = d.mean != null ? d.mean : d3.mean(d.values);

      const boxWidth = Math.min(x.bandwidth() * 0.2, 14);

      // Q1-Q3 box
      g.append('rect')
        .attr('x', cx - boxWidth / 2)
        .attr('y', y(q3))
        .attr('width', boxWidth)
        .attr('height', Math.max(0, y(q1) - y(q3)))
        .attr('fill', color)
        .attr('fill-opacity', 0.6)
        .attr('stroke', color)
        .attr('stroke-width', 1)
        .attr('rx', 2);

      // Median line
      g.append('line')
        .attr('x1', cx - boxWidth / 2)
        .attr('x2', cx + boxWidth / 2)
        .attr('y1', y(median))
        .attr('y2', y(median))
        .attr('stroke', '#fff')
        .attr('stroke-width', 2);

      // Mean dot
      g.append('circle')
        .attr('cx', cx)
        .attr('cy', y(mean))
        .attr('r', 3)
        .attr('fill', '#fff')
        .attr('stroke', color)
        .attr('stroke-width', 1.5);

      // Whiskers: min to Q1, Q3 to max
      const minVal = d3.min(d.values);
      const maxVal = d3.max(d.values);

      // Lower whisker
      g.append('line')
        .attr('x1', cx)
        .attr('x2', cx)
        .attr('y1', y(q1))
        .attr('y2', y(minVal))
        .attr('stroke', color)
        .attr('stroke-width', 1)
        .attr('stroke-opacity', 0.6);

      // Upper whisker
      g.append('line')
        .attr('x1', cx)
        .attr('x2', cx)
        .attr('y1', y(q3))
        .attr('y2', y(maxVal))
        .attr('stroke', color)
        .attr('stroke-width', 1)
        .attr('stroke-opacity', 0.6);

      // Invisible hover rect for tooltip
      g.append('rect')
        .attr('x', x(d.state))
        .attr('y', 0)
        .attr('width', x.bandwidth())
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            state: d.state,
            count: d.count != null ? d.count : d.values.length,
            median: median,
            mean: mean,
            q1: q1,
            q3: q3,
            min: minVal,
            max: maxVal,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).tickSize(0))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#374151')
      .attr('font-weight', 500);

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y).ticks(6).tickFormat((v) => `$${v}`))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Price ($)');

    return () => {
      svg.selectAll('*').remove();
    };
  }, [sorted]);

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Distribution by State (Violin)</div>
        <div style={styles.subtitle}>Kernel density estimation showing distribution shape per state</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Distribution by State (Violin)</div>
      <div style={styles.subtitle}>Kernel density estimation showing distribution shape per state</div>
      <div style={styles.container}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.state}
          </div>
          <div style={{ color: '#6b7280', marginBottom: '2px' }}>
            Addresses: {tooltip.count}
          </div>
          <div style={{ color: '#7c3aed', fontWeight: 600 }}>
            Median: ${tooltip.median.toFixed(2)}
          </div>
          <div style={{ color: '#2563eb', fontWeight: 600 }}>
            Mean: ${tooltip.mean.toFixed(2)}
          </div>
          <div style={{ color: '#6b7280', marginTop: '2px' }}>
            Q1: ${tooltip.q1.toFixed(2)} | Q3: ${tooltip.q3.toFixed(2)}
          </div>
          <div style={{ color: '#6b7280' }}>
            Min: ${tooltip.min.toFixed(2)} | Max: ${tooltip.max.toFixed(2)}
          </div>
        </div>
      )}
    </div>
  );
}
